import { endpoints } from "@/configs/Settings";
import { httpPost, type HttpError } from "src-next/shared/api/httpClient";

import {
  extractGraphApiErrorMessage,
  resolveHeaderMediaMimeType,
} from "../utils/whatsappResumableUpload";

const UPLOAD_TIMEOUT_MS = 120000;

function extractMessageMediaId(raw: unknown): string | null {
  if (!raw || typeof raw !== "object") return null;
  const envelope = raw as Record<string, unknown>;

  const directId = envelope.id;
  if (typeof directId === "string" && isMessageMediaId(directId)) {
    return directId.trim();
  }
  if (typeof directId === "number" && Number.isFinite(directId)) {
    return String(directId);
  }

  const data = envelope.data;
  if (data && typeof data === "object") {
    return extractMessageMediaId(data);
  }

  return null;
}

function isMessageMediaId(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith("upload:")) return false;
  if (trimmed.startsWith("4::")) return false;
  return true;
}

/**
 * Upload de mídia para envio de mensagem (não é o handle do template).
 * POST .../whatsappbusiness/{phoneNumberId}/media → { id }
 */
export async function uploadWhatsAppMessageMedia(params: {
  phoneNumberId: string;
  file: File;
}): Promise<string> {
  const phoneNumberId = params.phoneNumberId.trim();
  if (!phoneNumberId) {
    throw new Error("Phone Number ID é obrigatório para gerar o id de mídia.");
  }
  if (!params.file) {
    throw new Error("Arquivo de amostra é obrigatório.");
  }

  const fileType = resolveHeaderMediaMimeType(params.file);
  const url = `${String(endpoints.XDSB_domain ?? "").replace(/\/$/, "")}/whatsappbusiness/${encodeURIComponent(phoneNumberId)}/media`;

  const formData = new FormData();
  formData.append("messaging_product", "whatsapp");
  formData.append("file", params.file, params.file.name);
  formData.append("type", fileType);

  try {
    const response = await httpPost<unknown, FormData>(url, formData, {
      timeout: UPLOAD_TIMEOUT_MS,
      withCredentials: false,
      headers: {
        "Content-Type": undefined as unknown as string,
      },
    });

    const mediaId = extractMessageMediaId(response);
    if (!mediaId) {
      throw new Error("Upload de mídia concluído, mas a API não retornou o id.");
    }
    return mediaId;
  } catch (error) {
    const graphMessage = extractGraphApiErrorMessage((error as HttpError)?.data);
    if (graphMessage) throw new Error(graphMessage);
    throw error;
  }
}
